import cn from 'classnames'
import React from 'react'
import { createPortal } from 'react-dom'
import { usePopper } from 'react-popper'
import { useOnAnyClick } from '../src/hooks/useOnAnyClick'
import { useOnEscapeKey } from '../src/hooks/useOnEscapeKey'
import { Popup } from './Popup'

const FilterPreviewContext = React.createContext<
	(content: React.ReactNode) => void
>(() => null)

export const useSetFilterPreview = () => React.useContext(FilterPreviewContext)

export const sortObjectsWithStrings = function <T extends {}>(
	data: T[],
	getString: (item: T) => string,
) {
	return data.sort((a, b) => getString(a).localeCompare(getString(b), 'cs'))
}

export interface FilterProps {
	label: React.ReactNode
	options: React.ReactNode
	previewLabel?: React.ReactNode
	expandLabel?: React.ReactNode
}

export const Filter: React.FunctionComponent<FilterProps> = ({
	label,
	options,
	previewLabel,
	expandLabel,
}) => {
	const [isOpen, setIsOpen] = React.useState(false)
	const close = React.useCallback(() => setIsOpen(false), [setIsOpen])

	const [referenceElement, setReferenceElement] =
		React.useState<HTMLButtonElement | null>(null)
	const [popperElement, setPopperElement] = React.useState<HTMLElement | null>(
		null,
	)
	const { styles, attributes } = usePopper(referenceElement, popperElement, {
		strategy: 'fixed',
		placement: 'auto',
	})

	const previewRef = React.useRef<HTMLElement>(null)
	const [preview, setPreview] = React.useState<React.ReactNode>(null)

	useOnEscapeKey(close, isOpen)
	useOnAnyClick(close, isOpen, popperElement)

	return (
		<FilterPreviewContext.Provider value={setPreview}>
			<section
				className={cn('filter', isOpen && 'is-open', preview && 'is-active')}
			>
				<button
					type="button"
					className="filter-opener"
					onClick={() => setIsOpen(!isOpen)}
					ref={setReferenceElement}
				>
					<h6 className="filter-label">{label}</h6>
					<div className="filter-expand">
						{preview && (
							<span className="filter-preview" ref={previewRef}>
								{preview}
							</span>
						)}
						<span className="filter-expand-label">
							{expandLabel || 'Nastavit'}{' '}
						</span>
					</div>
				</button>
				{previewRef.current &&
					previewLabel &&
					createPortal(previewLabel, previewRef.current)}
				<div
					ref={setPopperElement}
					style={{ ...styles.popper, pointerEvents: 'none', zIndex: 2 }}
					{...attributes.popper}
				>
					<Popup isOpen={isOpen}>
						{options}
						<button onClick={close} className="filter-popupClose" type="button">
							Zavřít
						</button>
					</Popup>
				</div>
			</section>
		</FilterPreviewContext.Provider>
	)
}
